import * as React from 'react';

import {
  Button,
  Col,
  ConfirmPopoverButton,
  MultiSelect,
  Row,
  cn,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue } from 'jotai';
import { LucidePowerOff, LucideShield, LucideUsers } from 'lucide-react';

import {
  type FacilityRoleUserRecord,
  useFacilityRolesQuery,
} from './_graphql/useFacilityRolesQuery';
import { useRolesQuery } from './_graphql/useRolesQuery';
import { useUserRoleAssignMutation } from './_graphql/useUserRoleAssignMutation';
import { useUserRoleRevokeMutation } from './_graphql/useUserRoleRevokeMutation';

import {
  CopyToClipboard,
  DetailPanel,
  EmptyDetailState,
  FormFooter,
  PermissionsEditor,
  SelectionListItem,
  SettingsContent,
  TwoPanelSelectionLayout,
} from '~/modules/settings/_components/_shared';
import { allPermissionModules } from '~/modules/settings/_generated/permissions';
import {
  $currentFacility,
  $facilityDetailsSelectedUserId,
} from '~/modules/settings/_state/facilityDetailsForm.state';
import { useSettingsLocation } from '~/modules/settings/_state/settingsModule.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';

interface FacilityUserRoleAssignment {
  assignmentId: string;
  roleId: string;
  roleName: string;
  permissions: string[];
}

interface FacilityUserRecord {
  id: string;
  name: string;
  email: string;
  phoneNumber: string | null;
  roleAssignments: FacilityUserRoleAssignment[];
}

const UserInitialsAvatar: React.FC<{ name: string }> = ({ name }) => {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="@UserInitialsAvatar flex size-[54px] shrink-0 items-center justify-center rounded-full bg-primary/10 text-lg font-bold text-primary">
      {initials}
    </div>
  );
};

interface FacilityUserDetailPanelProps {
  user: FacilityUserRecord;
  facilityId: string;
  allRoles: { id: string; name: string }[];
  onGoToUserSettings: () => void;
  onRemoveAccess: () => Promise<void>;
  isRemoving: boolean;
}

const FacilityUserDetailPanel: React.FC<FacilityUserDetailPanelProps> = ({
  user,
  facilityId,
  allRoles,
  onGoToUserSettings,
  onRemoveAccess,
  isRemoving,
}) => {
  const t = useTranslations();
  const { toast } = useToast();
  const assignMutation = useUserRoleAssignMutation();
  const revokeMutation = useUserRoleRevokeMutation();
  const [isSaving, setIsSaving] = React.useState(false);

  const currentRoleIds = React.useMemo(
    () => user.roleAssignments.map((a) => a.roleId),
    [user.roleAssignments],
  );

  const [draftRoleIds, setDraftRoleIds] =
    React.useState<string[]>(currentRoleIds);

  React.useEffect(() => {
    setDraftRoleIds(currentRoleIds);
  }, [currentRoleIds]);

  const isDirty = React.useMemo(() => {
    if (draftRoleIds.length !== currentRoleIds.length) return true;
    const sorted1 = [...draftRoleIds].sort();
    const sorted2 = [...currentRoleIds].sort();
    return sorted1.some((id, i) => id !== sorted2[i]);
  }, [draftRoleIds, currentRoleIds]);

  const roleOptions = React.useMemo(
    () =>
      allRoles.map((role) => ({
        key: role.id,
        label: role.name,
      })),
    [allRoles],
  );

  const permissionsValue = React.useMemo(() => {
    const value: Record<string, boolean> = {};
    const selectedRoleIds = new Set(draftRoleIds);
    user.roleAssignments.forEach((assignment) => {
      if (selectedRoleIds.has(assignment.roleId)) {
        assignment.permissions.forEach((key) => {
          value[key] = true;
        });
      }
    });
    return value;
  }, [user.roleAssignments, draftRoleIds]);

  const handleDiscard = () => {
    setDraftRoleIds(currentRoleIds);
  };

  const handleSave = async () => {
    const added = draftRoleIds.filter((id) => !currentRoleIds.includes(id));
    const removed = currentRoleIds.filter((id) => !draftRoleIds.includes(id));

    setIsSaving(true);
    try {
      const promises: Promise<unknown>[] = [];

      for (const roleId of added) {
        promises.push(
          assignMutation.mutateAsync({
            userId: user.id,
            roleId,
            facilityId,
          }),
        );
      }

      for (const roleId of removed) {
        const assignment = user.roleAssignments.find(
          (a) => a.roleId === roleId,
        );
        if (assignment) {
          promises.push(
            revokeMutation.mutateAsync({
              assignmentId: assignment.assignmentId,
            }),
          );
        }
      }

      await Promise.all(promises);
      toast({
        title: t.teamAndPermissions.facilities.details.users.rolesUpdated,
        variant: 'success',
      });
    } catch (err) {
      toast({
        title: t.teamAndPermissions.facilities.details.users.rolesUpdateError,
        description: err instanceof Error ? err.message : undefined,
        variant: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DetailPanel
      header={
        <div className="px-8 pt-5">
          <Row className="items-center gap-3">
            <UserInitialsAvatar name={user.name} />
            <Col className="min-w-0 flex-1 gap-1">
              <span className="text-lg font-bold text-neutral">
                {user.name}
              </span>
              <Row className="items-center gap-1.5">
                {user.email && (
                  <CopyToClipboard
                    text={user.email}
                    copyLabel="Copy email"
                    copiedLabel="Email copied"
                  />
                )}
                {user.email && user.phoneNumber && (
                  <span className="text-sm text-neutral/40">・</span>
                )}
                {user.phoneNumber && (
                  <CopyToClipboard
                    text={user.phoneNumber}
                    copyLabel="Copy phone"
                    copiedLabel="Phone copied"
                  />
                )}
              </Row>
            </Col>
            <Row className="shrink-0 gap-2">
              <Button variant="outline" onPress={onGoToUserSettings}>
                {
                  t.teamAndPermissions.facilities.details.actions
                    .goToUserSettings
                }
              </Button>
              <ConfirmPopoverButton
                onConfirm={onRemoveAccess}
                message={t.teamAndPermissions.facilities.details.users.removeFromFacilityDialog.description(
                  user.name,
                )}
                confirmLabel={
                  t.teamAndPermissions.facilities.details.users
                    .removeFromFacilityDialog.confirm
                }
                cancelLabel={
                  t.teamAndPermissions.facilities.details.users
                    .removeFromFacilityDialog.cancel
                }
                title={t.teamAndPermissions.facilities.details.users.removeFromFacilityDialog.title(
                  user.name,
                )}
                placement="bottom end"
                contentClassName="max-w-md"
                renderTrigger={(props) => (
                  <Button
                    {...props}
                    variant="outline"
                    color="failure"
                    isDisabled={isRemoving}
                    isLoading={isRemoving}
                  >
                    <LucidePowerOff className="size-4" />
                    {t.teamAndPermissions.facilities.details.users.removeAccess}
                  </Button>
                )}
              />
            </Row>
          </Row>
        </div>
      }
      contentClassName="gap-6"
      footer={
        <FormFooter
          onDiscard={handleDiscard}
          onSave={handleSave}
          isDiscardDisabled={isSaving || !isDirty}
          isSaveDisabled={isSaving || !isDirty}
          isSaveLoading={isSaving}
          discardLabel={t.teamAndPermissions.actions.discard}
          saveLabel={t.teamAndPermissions.actions.save}
        />
      }
      className="@FacilityUserDetailPanel"
    >
      <div className="w-[450px] max-w-full">
        <MultiSelect
          label={t.teamAndPermissions.facilities.details.sections.roles}
          required
          options={roleOptions}
          selectedKeys={draftRoleIds}
          onSelectionChange={setDraftRoleIds}
          placeholder={t.teamAndPermissions.search.placeholder}
        />
      </div>

      <PermissionsEditor
        modules={allPermissionModules}
        value={permissionsValue}
        readOnly
      />
    </DetailPanel>
  );
};

export const FacilityUsersTab = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();
  const currentFacility = useAtomValue($currentFacility);
  const [selectedUserId, setSelectedUserId] = useAtom(
    $facilityDetailsSelectedUserId,
  );

  const { toast } = useToast();
  const revokeMutation = useUserRoleRevokeMutation();

  const [searchValue, setSearchValue] = React.useState('');

  const { data } = useFacilityRolesQuery({
    facilityId: currentFacility?.id ?? '',
    limit: 1000,
    offset: 0,
  });

  const { data: rolesData } = useRolesQuery({
    limit: 1000,
    offset: 0,
  });

  const roles = data?.roles ?? [];
  const usersMap = data?.users ?? new Map<string, FacilityRoleUserRecord[]>();

  const availableRoles = React.useMemo(
    () =>
      (rolesData?.roles ?? [])
        .filter((r) => r.status === 'active')
        .map((r) => ({ id: r.id, name: r.name })),
    [rolesData],
  );

  const allUsers = React.useMemo((): FacilityUserRecord[] => {
    const userMap = new Map<string, FacilityUserRecord>();

    roles.forEach((role) => {
      const roleUsers = usersMap.get(role.id) ?? [];
      roleUsers.forEach((user) => {
        const existing = userMap.get(user.id);
        const assignment: FacilityUserRoleAssignment = {
          assignmentId: user.assignmentId,
          roleId: role.id,
          roleName: role.name,
          permissions: role.permissions,
        };

        if (existing) {
          existing.roleAssignments.push(assignment);
        } else {
          userMap.set(user.id, {
            id: user.id,
            name: user.name,
            email: user.email,
            phoneNumber: user.phoneNumber,
            roleAssignments: [assignment],
          });
        }
      });
    });

    return Array.from(userMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [roles, usersMap]);

  React.useEffect(() => {
    if (!selectedUserId && allUsers.length > 0) {
      setSelectedUserId(allUsers[0].id);
    }
  }, [selectedUserId, allUsers, setSelectedUserId]);

  const filteredUsers = React.useMemo(
    () =>
      allUsers.filter((user) =>
        user.name.toLowerCase().includes(searchValue.toLowerCase()),
      ),
    [allUsers, searchValue],
  );

  const handleGoToUserSettings = (userId: string) => {
    setLocation(`/team-and-permissions/users/${userId}` as never);
  };

  const handleRemoveAccess = async (user: FacilityUserRecord) => {
    try {
      for (const assignment of user.roleAssignments) {
        await revokeMutation.mutateAsync({
          assignmentId: assignment.assignmentId,
        });
      }
      setSelectedUserId(null);
      toast({
        title:
          t.teamAndPermissions.facilities.details.users.removeFromFacilityDialog
            .success,
        variant: 'success',
      });
    } catch (err) {
      toast({
        title:
          t.teamAndPermissions.facilities.details.users.removeFromFacilityDialog
            .error,
        description: err instanceof Error ? err.message : undefined,
        variant: 'error',
      });
    }
  };

  return (
    <SettingsContent size="full" className="@FacilityUsersTab h-full">
      <TwoPanelSelectionLayout
        items={filteredUsers}
        selectedId={selectedUserId}
        onSelect={setSelectedUserId}
        getItemId={(user) => user.id}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder={t.teamAndPermissions.search.placeholder}
        leftPanelWidth={240}
        renderItem={(user, isSelected) => (
          <div className="px-2">
            <SelectionListItem
              name={user.name}
              tagIcon={<LucideShield size={14} />}
              tagCount={user.roleAssignments.length}
              isSelected={isSelected}
              className={cn(
                user.roleAssignments.length === 0 &&
                  !isSelected &&
                  'opacity-60',
              )}
            />
          </div>
        )}
        renderDetail={(user) =>
          user ? (
            <FacilityUserDetailPanel
              key={user.id}
              user={user}
              facilityId={currentFacility?.id ?? ''}
              allRoles={availableRoles}
              onGoToUserSettings={() => handleGoToUserSettings(user.id)}
              onRemoveAccess={() => handleRemoveAccess(user)}
              isRemoving={revokeMutation.isPending}
            />
          ) : (
            <EmptyDetailState
              icon={<LucideUsers />}
              title={
                t.teamAndPermissions.facilities.details.users.selectEmptyState
              }
              subtitle={
                t.teamAndPermissions.facilities.details.users
                  .selectEmptyStateSubtitle
              }
            />
          )
        }
      />
    </SettingsContent>
  );
};
